import { useState, useEffect, useCallback } from 'react';
import { Upload, FileText, ArrowClockwise, Check, Warning, Trash, FolderOpen, DownloadSimple } from "@phosphor-icons/react";
import { toast } from 'sonner';
import api from '@/lib/api';

export default function UploadsPage() {
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null);

  const fetchFolders = useCallback(async () => {
    try {
      const res = await api.get('/uploads/folders');
      setFolders(res.data.folders || []);
    } catch (err) {
      console.error('Failed to load folders', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchFolders(); }, [fetchFolders]);

  const handleUpload = async (folderId, e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(folderId);
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const formData = new FormData();
      formData.append('file', file);
      try {
        await api.post('/uploads/folders/' + folderId + '/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success(file.name + ' hochgeladen');
      } catch (err) {
        toast.error(file.name + ': Upload fehlgeschlagen');
      }
    }
    fetchFolders();
    setUploading(null);
    e.target.value = '';
  };

  const handleDelete = async (folderId, filename) => {
    if (!window.confirm('Datei "' + filename + '" wirklich loeschen?')) return;
    try {
      await api.delete('/uploads/folders/' + folderId + '/' + filename);
      toast.success(filename + ' geloescht');
      fetchFolders();
    } catch (err) {
      toast.error('Loeschen fehlgeschlagen');
    }
  };

  const formatSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes > 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    return (bytes / 1024).toFixed(0) + ' KB';
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    return d.toLocaleDateString('de-DE') + ' ' + d.toLocaleTimeString('de-DE', {hour:'2-digit', minute:'2-digit'});
  };

  const folderStyles = {
    'AVB': { border: 'border-blue-600/30', bg: 'bg-blue-900/10', icon: 'text-blue-400' },
    'TSRID': { border: 'border-purple-600/30', bg: 'bg-purple-900/10', icon: 'text-purple-400' },
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Uploads</h1>
          <p className="text-sm text-[#888] mt-1">Dateien die bei der Installation auf Remote-Geraete verteilt werden</p>
        </div>
        <button onClick={fetchFolders} className="p-2 rounded-lg bg-[#2A2A2A] hover:bg-[#3A3A3A] text-[#888] hover:text-white transition-colors">
          <ArrowClockwise size={18} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {folders.map(folder => {
        const style = folderStyles[folder.id] || { border: 'border-[#3A3A3A]', bg: 'bg-[#1E1E1E]', icon: 'text-[#888]' };
        return (
          <div key={folder.id} className={'rounded-xl border-2 overflow-hidden ' + style.border + ' ' + style.bg}>
            <div className="px-6 py-4 flex items-center justify-between border-b border-[#3A3A3A]/50">
              <div className="flex items-center gap-3">
                <FolderOpen size={24} className={style.icon} weight="duotone" />
                <div>
                  <h2 className="text-base font-bold text-white">{folder.label}</h2>
                  <p className="text-xs text-[#888]">{folder.description}</p>
                </div>
              </div>
              <label className="cursor-pointer">
                <input type="file" multiple className="hidden" onChange={(e) => handleUpload(folder.id, e)} />
                <div className={'flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ' + (uploading === folder.id ? 'bg-yellow-900/30 text-yellow-400' : 'bg-[#D50C2D] hover:bg-[#E51D3E] text-white')}>
                  {uploading === folder.id ? (
                    <span className="flex items-center gap-2"><ArrowClockwise size={16} className="animate-spin" /> Uploading...</span>
                  ) : (
                    <span className="flex items-center gap-2"><Upload size={16} /> Upload</span>
                  )}
                </div>
              </label>
            </div>

            {folder.files.length === 0 ? (
              <div className="px-6 py-8 text-center">
                <Warning size={32} className="text-[#555] mx-auto mb-2" />
                <p className="text-sm text-[#666]">Keine Dateien vorhanden</p>
              </div>
            ) : (
              <div className="divide-y divide-[#3A3A3A]/30">
                {folder.files.map(file => (
                  <div key={file.name} className="px-6 py-3 flex items-center justify-between hover:bg-[#ffffff05] transition-colors">
                    <div className="flex items-center gap-3">
                      <FileText size={20} className="text-green-400" weight="duotone" />
                      <div>
                        <span className="text-sm font-medium text-white">{file.name}</span>
                        <div className="flex items-center gap-3 mt-0.5">
                          <span className="text-[10px] text-[#888]">{formatSize(file.size)}</span>
                          <span className="text-[10px] text-[#888]">{formatDate(file.modified)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <a href={process.env.REACT_APP_BACKEND_URL + '/api/uploads/folders/' + folder.id + '/' + file.name + '/download'}
                         className="p-1.5 rounded hover:bg-[#3A3A3A] text-[#888] hover:text-cyan-400 transition-colors" title="Download">
                        <DownloadSimple size={16} />
                      </a>
                      <button onClick={() => handleDelete(folder.id, file.name)}
                              className="p-1.5 rounded hover:bg-red-900/30 text-[#888] hover:text-red-400 transition-colors" title="Loeschen">
                        <Trash size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
